import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  MenuItem,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { LineItemProvisioningState } from '../types/order';
import api from '../services/api';

interface StaticIpProvisionModalProps {
  open: boolean;
  orderId: string;
  cartLineIndex: number;
  accountLinked: boolean;
  /** True when an IP was already recorded (edit mode). */
  isComplete: boolean;
  onClose: () => void;
  onSuccess: (updated: LineItemProvisioningState) => void;
}

/** Manual Static IP provisioning: record the assigned IP; this creates the service on the customer's account. */
export default function StaticIpProvisionModal({
  open,
  orderId,
  cartLineIndex,
  accountLinked,
  isComplete,
  onClose,
  onSuccess,
}: StaticIpProvisionModalProps) {
  const [ip, setIp] = useState('');
  const [notes, setNotes] = useState('');
  const [linked, setLinked] = useState('');
  const [options, setOptions] = useState<{ id: string; label: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setIp('');
    setNotes('');
    setLinked('');
    setOptions([]);
    setLoading(true);
    api
      .adminGetStaticIpAssignment(orderId, cartLineIndex)
      .then((d) => {
        setOptions(d.linkable_services || []);
        setIp(d.ip_address || '');
        setNotes(d.notes || '');
        setLinked(d.linked_service_id || d.suggested_service_id || '');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, orderId, cartLineIndex, isComplete]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await api.adminAssignStaticIp(orderId, cartLineIndex, {
        ip_address: ip.trim(),
        linked_service_id: linked || null,
        notes: notes.trim() || null,
      });
      onSuccess(updated);
      onClose();
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to save the IP address.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{isComplete ? 'Edit assigned IP address' : 'Assign IP address'}</DialogTitle>
      <DialogContent dividers>
        {!accountLinked && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Link this order to a customer account first. The Static IP is added to that account as a service.
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? (
          <CircularProgress size={24} />
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter the IP address you have assigned to this customer. Saving adds a Static IP service to their
              account and completes this step.
            </Typography>
            <TextField
              label="Assigned IP address"
              value={ip}
              onChange={(e) => setIp(e.target.value)}
              fullWidth
              autoFocus
              required
              placeholder="203.0.113.10 or 203.0.113.8/29"
              helperText="A single address, or a block in CIDR form"
              sx={{ mb: 2 }}
            />
            <TextField
              select
              label="Assigned to service"
              value={linked}
              onChange={(e) => setLinked(e.target.value)}
              fullWidth
              required={options.length > 0}
              disabled={options.length === 0}
              error={options.length > 0 && !linked}
              helperText={
                options.length === 0
                  ? "This customer has no active services to assign the IP to, so it will be saved without a link."
                  : 'The active service on the customer\'s account that this IP is assigned to'
              }
              sx={{ mb: 2 }}
            >
              {options.map((o) => (
                <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || loading || !ip.trim() || !accountLinked || (options.length > 0 && !linked)}>
          {saving ? <CircularProgress size={20} /> : isComplete ? 'Save changes' : 'Assign and add service'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
